import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { CarType } from '../../enum';
import { Cars, WidgetConfig } from '../../types';
import { isValidArray } from '../../utils/validation';
import { getAssetURL } from '../../utils/url';

const getPhotoPosition = (position?: number | string | null) => {
  if (position === null || position === undefined || position === '') {
    return Number.MAX_SAFE_INTEGER;
  }

  const numericPosition = Number(position);

  return Number.isNaN(numericPosition) ? Number.MAX_SAFE_INTEGER : numericPosition;
};

const getFirstPhotoUrl = (
  assetsUrl: string,
  car?: Cars
) => {
  if (!car || !isValidArray(car.photos)) {
    return undefined;
  }

  const [firstPhoto] = [...car.photos].sort((first, second) => {
    const firstPosition = getPhotoPosition(first.position);
    const secondPosition = getPhotoPosition(second.position);

    return firstPosition - secondPosition;
  });

  if (!firstPhoto) {
    return undefined;
  }

  return getAssetURL(assetsUrl, firstPhoto.key, firstPhoto.url);
};

interface PropertyTypeSelectionDialogProps {
  config: WidgetConfig;
  onCarTypeSelected: (carType: CarType) => void;
  onBack?: () => void;
  open: boolean;
  onClose: () => void;
  dealerRecentCars: Cars[],
}

const PropertyTypeSelectionDialog: React.FC<PropertyTypeSelectionDialogProps> = ({
  config,
  onCarTypeSelected,
  onBack,
  open,
  onClose,
  dealerRecentCars,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const options = [
    {
      type: CarType.new,
      title: 'New Vehicles',
      description: 'Latest models from this dealership',
      buttonLabel: 'New Vehicles',
      imageUrl: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_maroon_cadillac_rounded.png',
    },
    {
      type: CarType.used,
      title: 'Used Vehicles',
      description: 'Pre-owned vehicles at different price points',
      buttonLabel: 'Used Vehicles',
      imageUrl: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_used_vehicle.png',
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            // bgcolor: '#E8EBF4',
            // backgroundImage: 'none',
            // maxHeight: isMobile ? '100vh' : '95vh',
            // m: isMobile ? 0 : 2,
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(226,234,246,0.96))',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
          }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 40px',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {/* Left spacer to keep center aligned */}
              <Box />

              {/* Center content */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 18, sm: 20 },
                    color: '#2b2f36',
                    lineHeight: 1.2,
                  }}
                >
                  What are you shopping for?
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: 12, sm: 13 },
                    color: '#7a8392',
                    mt: 0.5,
                  }}
                >
                  Select one to continue
                </Typography>
              </Box>

              {/* Back button */}
              {onBack ? (
                <IconButton
                  onClick={onBack}
                  aria-label='Back'
                  sx={{
                    justifySelf: 'end',
                    bgcolor: 'rgba(0,0,0,0.04)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              ) : (
                <Box />
              )}
            </Box>

            <Grid container spacing={2}>
              {options.map((option, idx) => (
                <Grid key={option.type} size={{ xs: 12, sm: 6 }}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid rgba(0,0,0,0.08)',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.12)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        height: { xs: 140, sm: 160 },
                        backgroundImage: `url(${getFirstPhotoUrl(
                          config.assetsUrl,
                          dealerRecentCars[idx]
                        ) ?? option.imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background:
                            'linear-gradient(180deg, rgba(255,255,255,0.15), rgba(0,0,0,0.08))',
                        }}
                      />
                    </Box>
                    <CardContent
                      sx={{
                        p: { xs: 2, sm: 2.5 },
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#2b2f36',
                          fontSize: { xs: '16px', sm: '17px' },
                        }}
                      >
                        {option.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#333333',
                          fontSize: { xs: '12px', sm: '13px' },
                          minHeight: isMobile ? 'auto' : 36,
                        }}
                      >
                        {option.description}
                      </Typography>
                      <Button
                        fullWidth
                        variant='contained'
                        onClick={() => onCarTypeSelected(option.type)}
                        sx={{
                          mt: 'auto',
                          textTransform: 'none',
                          fontWeight: 700,
                          bgcolor: '#2a80f5',
                          '&:hover': { bgcolor: '#1d6fe0' },
                          borderRadius: 1.5,
                          py: 0.9,
                        }}
                      >
                        {option.buttonLabel}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Box>
      </Dialog>
    </>
  );
};

export default PropertyTypeSelectionDialog;
